if (!customElements.get('cart-upsell-widget')) {
  class CartUpsellWidget extends HTMLElement {
    constructor() {
      super();

      this.upsellProducts = JSON.parse(this.getAttribute('data-upsell-products') || '[]');
      this.currentProductIndex = 0;
      this.isCartDrawer = this.hasAttribute('data-cart-drawer');
      this.isCartPage = this.hasAttribute('data-cart-page');

      this.widget = this.querySelector('[data-upsell-widget]');
      this.productImage = this.querySelector('[data-upsell-image]');
      this.productLink = this.querySelector('[data-upsell-link]');
      this.productTitle = this.querySelector('[data-upsell-title]');
      this.productDescription = this.querySelector('[data-upsell-description]');
      this.productPrice = this.querySelector('[data-upsell-price]');
      this.addButton = this.querySelector('[data-upsell-add-button]');
      this.buttonText = this.querySelector('[data-upsell-button-text]');
      this.titleMain = this.querySelector('.cart-upsell__title-main');

      this.#init();
    }

    disconnectedCallback() {
      if (this.cartObserver) {
        this.cartObserver.disconnect();
      }

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
    }

    async #init() {
      if (this.isCartDrawer) {
        this.#initializeCartForm();
      }

      await this.#updateCurrentProduct();
      this.#bindEvents();
      this.setAttribute('data-initialized', 'true');
    }

    #initializeCartForm() {
      const cartDrawerForm = document.querySelector('#CartDrawerForm');
      if (cartDrawerForm && !cartDrawerForm.cartFormInstance && window.theme && window.theme.CartForm) {
        cartDrawerForm.cartFormInstance = new window.theme.CartForm(cartDrawerForm);
      }
    }

    #bindEvents() {
      if (this.addButton) {
        this.addButton.addEventListener('click', this.#handleAddToCart.bind(this));
      }

      document.addEventListener('theme:cart:change', this.#handleCartChange.bind(this));
      document.addEventListener('cart:updated', this.#handleCartChange.bind(this));

      document.addEventListener('cart:quantity', this.#handleCartChange.bind(this));
      this.#setupCartObserver();
    }

    #setupCartObserver() {
      const cartProductsContainer = document.querySelector('[data-products]');
      if (cartProductsContainer && window.MutationObserver) {
        this.cartObserver = new MutationObserver((mutations) => {
          let shouldUpdate = false;

          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' ||
                (mutation.type === 'attributes' &&
                 (mutation.attributeName === 'data-count' || mutation.attributeName === 'data-cart-subtotal'))) {
              shouldUpdate = true;
            }
          });

          if (shouldUpdate) {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = setTimeout(() => {
              this.#updateCurrentProduct();
            }, 300);
          }
        });
        this.cartObserver.observe(cartProductsContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-count', 'data-cart-subtotal']
        });
      }
    }

    #restartCartObserver() {
      if (this.cartObserver) {
        this.cartObserver.disconnect();
      }
      setTimeout(() => {
        this.#setupCartObserver();
      }, 100);
    }

    async #updateCurrentProduct() {
      if (this.upsellProducts.length === 0) {
        this.#hideWidget();
        return;
      }

      try {
        const cart = await this.#getCart();
        const availableProducts = await this.#filterAvailableProducts(cart);

        if (availableProducts.length === 0) {
          this.#hideWidget();
          return;
        }

        const currentProduct = availableProducts[0];
        await this.#renderProduct(currentProduct);
        this.#showWidget();
      } catch (error) {
        this.#hideWidget();
      }
    }

    async #filterAvailableProducts(cart) {
      const cartProductIds = cart.items.map(item => item.product_id.toString());

      return this.upsellProducts.filter(product =>
        !cartProductIds.includes(product.id.toString())
      );
    }

    async #renderProduct(product) {
      if (!product) return;
      if (this.productImage && product.featured_image) {
        this.productImage.src = product.featured_image;
        this.productImage.alt = product.title;
      }
      if (this.productLink) {
        this.productLink.href = product.url;
      }
      if (this.productTitle) {
        this.productTitle.textContent = product.title;
        this.productTitle.href = product.url;
      }
      if (this.productDescription) {
        this.productDescription.textContent = product.description ?
          this.#truncateText(product.description, 100) : '';
      }
      if (this.productPrice && product.price) {
        this.productPrice.innerHTML = this.#formatPrice(product.price, product.compare_at_price);
      }
      if (this.addButton) {
        this.addButton.setAttribute('data-variant-id', product.variants[0]?.id || '');
        this.addButton.disabled = !product.available;

        if (this.buttonText) {
          this.buttonText.textContent = product.available ?
            this.getAttribute('data-add-text') || 'Add to cart' :
            this.getAttribute('data-sold-out-text') || 'Sold out';
        }
      }
    }

    async #handleAddToCart(event) {
      event.preventDefault();

      const variantId = this.addButton.getAttribute('data-variant-id');
      if (!variantId || this.addButton.disabled) return;

      this.#setLoadingState(true);

      try {
        let cartData;
        if (window.theme && window.theme.cart && window.theme.cart._updateCart) {
          const result = await window.theme.cart._updateCart({
            url: '/cart/add.js',
            data: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });
          cartData = JSON.parse(result);
        } else {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          cartData = await response.json();
        }
        if (this.isCartDrawer) {
          await this.#updateCartDrawerSmooth();
        } else {
        }
        document.dispatchEvent(new CustomEvent('theme:cart:change', {
          detail: { cart: await this.#getCart() }
        }));

        this.#showSuccess();
        setTimeout(() => {
          this.#updateCurrentProduct();
        }, 1500);
        if (!this.isCartDrawer && !this.isCartPage) {
          await this.#showPopdownNotification(cartData);
        }
        if (this.isCartPage) {
          await this.#updateCartPageSmooth();
        }

      } catch (error) {
        this.#showError();
      } finally {
        this.#setLoadingState(false);
      }
    }

    async #handleCartChange() {
      await this.#updateCurrentProduct();
    }

    async #getCart() {
      try {
        const response = await fetch('/cart.js');
        return await response.json();
      } catch (error) {
        return { items: [] };
      }
    }

    async #updateCartDrawerSmooth() {
      try {
        const cartDrawerForm = document.querySelector('#CartDrawerForm');
        if (!cartDrawerForm) {
          return;
        }
        if (!cartDrawerForm.cartFormInstance && window.theme && window.theme.CartForm) {
          cartDrawerForm.cartFormInstance = new window.theme.CartForm(cartDrawerForm);
        }
        if (window.theme && window.theme.cart && window.theme.cart.getCartProductMarkup) {
          const html = await window.theme.cart.getCartProductMarkup();
          if (!html) {
            return;
          }
          const cartFormInstance = cartDrawerForm.cartFormInstance;
          if (cartFormInstance && cartFormInstance.cartMarkup) {
            cartFormInstance.cartMarkup(html);
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newProducts = doc.querySelector('.cart__items');
            if (newProducts) {
              const cartCount = newProducts.getAttribute('data-count') || '0';
              this.#updateHeaderCartCount(cartCount);
            }
            return;
          }
        }
        await this.#manualCartUpdate();

      } catch (error) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }

    async #manualCartUpdate() {
      const html = await window.theme.cart.getCartProductMarkup();
      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productsContainer = document.querySelector('[data-products]');
      const newProducts = doc.querySelector('.cart__items');

      if (productsContainer && newProducts) {
        productsContainer.innerHTML = newProducts.innerHTML;
        productsContainer.setAttribute('data-count', newProducts.getAttribute('data-count') || '0');
        productsContainer.setAttribute('data-cart-subtotal', newProducts.getAttribute('data-cart-subtotal') || '0');
      }
      const discountsContainer = document.querySelector('[data-discounts]');
      const newDiscounts = doc.querySelector('.cart__discounts');
      if (discountsContainer && newDiscounts) {
        discountsContainer.innerHTML = newDiscounts.outerHTML;
      }
      const subtotalElement = document.querySelector('[data-subtotal]');
      if (subtotalElement && newProducts) {
        const newSubtotal = newProducts.getAttribute('data-cart-subtotal');
        if (newSubtotal && window.theme && window.theme.Currency && window.theme.settings) {
          subtotalElement.innerHTML = window.theme.Currency.formatMoney(newSubtotal, window.theme.settings.moneyFormat);
        }
      }
      const cartCount = newProducts.getAttribute('data-count') || '0';
      this.#updateHeaderCartCount(cartCount);
      this.#reinitializeCartFunctionality();
      this.#restartCartObserver();
    }

    #updateHeaderCartCount(cartCount) {
      const countElements = document.querySelectorAll('.cart-link__bubble-num');
      countElements.forEach(el => {
        el.textContent = cartCount;
      });
      const bubbles = document.querySelectorAll('.cart-link__bubble');
      bubbles.forEach(bubble => {
        if (parseInt(cartCount) > 0) {
          bubble.classList.add('cart-link__bubble--visible');
        } else {
          bubble.classList.remove('cart-link__bubble--visible');
        }
      });
    }

    async #updateCartPageSmooth() {
      try {
        const cartPageForm = document.querySelector('#CartPageForm');
        if (!cartPageForm) {
          return;
        }
        if (!cartPageForm.cartFormInstance && window.theme && window.theme.CartForm) {
          cartPageForm.cartFormInstance = new window.theme.CartForm(cartPageForm);
        }
        if (window.theme && window.theme.cart && window.theme.cart.getCartProductMarkup) {
          const html = await window.theme.cart.getCartProductMarkup();
          if (!html) {
            return;
          }
          const cartFormInstance = cartPageForm.cartFormInstance;
          if (cartFormInstance && cartFormInstance.cartMarkup) {
            cartFormInstance.cartMarkup(html);
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newProducts = doc.querySelector('.cart__items');
            if (newProducts) {
              const cartCount = newProducts.getAttribute('data-count') || '0';
              this.#updateHeaderCartCount(cartCount);
            }
            return;
          }
        }
        await this.#manualCartPageUpdate();

      } catch (error) {
      }
    }

    async #manualCartPageUpdate() {
      const html = await window.theme.cart.getCartProductMarkup();
      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productsContainer = document.querySelector('[data-products].cart__page-col');
      const newProducts = doc.querySelector('.cart__items');

      if (productsContainer && newProducts) {
        productsContainer.innerHTML = newProducts.innerHTML;
      }
      const discountsContainer = document.querySelector('[data-discounts]');
      const newDiscounts = doc.querySelector('.cart__discounts');
      if (discountsContainer && newDiscounts) {
        discountsContainer.innerHTML = newDiscounts.outerHTML;
      }
      const subtotalElement = document.querySelector('[data-subtotal]');
      if (subtotalElement && newProducts) {
        const newSubtotal = newProducts.getAttribute('data-cart-subtotal');
        if (newSubtotal && window.theme && window.theme.Currency && window.theme.settings) {
          subtotalElement.innerHTML = window.theme.Currency.formatMoney(newSubtotal, window.theme.settings.moneyFormat);
        }
      }
      const cartCount = newProducts.getAttribute('data-count') || '0';
      this.#updateHeaderCartCount(cartCount);
      this.#reinitializeCartPageFunctionality();
      this.#restartCartObserver();
    }

    #reinitializeCartPageFunctionality() {
      try {
        const qtySelectors = document.querySelectorAll('.js-qty__wrapper');
        qtySelectors.forEach(qtyWrapper => {
          const existingSelector = qtyWrapper.qtySelector;
          if (existingSelector && existingSelector.destroy) {
            existingSelector.destroy();
          }
          if (window.theme && window.theme.QtySelector) {
            qtyWrapper.qtySelector = new window.theme.QtySelector(qtyWrapper, {
              namespace: '.cart-page'
            });
          }
        });
        const removeLinks = document.querySelectorAll('.cart__remove a');
        removeLinks.forEach(link => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const href = link.getAttribute('href');
            const match = href.match(/id=([^&]+)/);
            if (match) {
              const variantId = match[1];
              if (window.theme && window.theme.cart && window.theme.cart.changeItem) {
                window.theme.cart.changeItem(variantId, 0).then(() => {
                  this.#updateCartPageSmooth();
                });
              }
            }
          });
        });
      } catch (error) {
      }
    }

    #reinitializeCartFunctionality() {
      try {
        const qtySelectors = document.querySelectorAll('.js-qty__wrapper');
        qtySelectors.forEach(qtyWrapper => {
          const existingSelector = qtyWrapper.qtySelector;
          if (existingSelector) {
            existingSelector.destroy();
          }
          if (window.theme && window.theme.QtySelector) {
            qtyWrapper.qtySelector = new window.theme.QtySelector(qtyWrapper, {
              namespace: '.cart-drawer'
            });
          }
        });
        const removeLinks = document.querySelectorAll('.cart__remove a');
        removeLinks.forEach(link => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const href = link.getAttribute('href');
            const match = href.match(/id=([^&]+)/);
            if (match) {
              const variantId = match[1];
              if (window.theme && window.theme.cart && window.theme.cart.changeItem) {
                window.theme.cart.changeItem(variantId, 0).then(() => {
                  this.#updateCartDrawerSmooth();
                });
              }
            }
          });
        });
      } catch (error) {
      }
    }

    async #updateCartDrawer() {
      try {
        const cartForm = document.querySelector('#CartDrawerForm');
        if (cartForm && cartForm.cartFormInstance) {
          document.dispatchEvent(new CustomEvent('cart:build'));
          return;
        }
        if (window.theme && window.theme.cart && window.theme.cart.getCartProductMarkup) {
          const html = await window.theme.cart.getCartProductMarkup();
          if (!html) {
            return;
          }
          const cartDrawerForm = document.querySelector('#CartDrawerForm');
          if (cartDrawerForm && window.theme.CartForm) {
            let cartFormInstance = cartDrawerForm.cartFormInstance;
            if (!cartFormInstance) {
              cartFormInstance = new window.theme.CartForm(cartDrawerForm);
              cartDrawerForm.cartFormInstance = cartFormInstance;
            }
            if (cartFormInstance && cartFormInstance.cartMarkup) {
              cartFormInstance.cartMarkup(html);
              return;
            }
          }
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          const productsContainer = document.querySelector('[data-products]');
          const newProducts = doc.querySelector('.cart__items');

          if (productsContainer && newProducts) {
            productsContainer.innerHTML = newProducts.innerHTML;
            productsContainer.setAttribute('data-count', newProducts.getAttribute('data-count') || '0');
            productsContainer.setAttribute('data-cart-subtotal', newProducts.getAttribute('data-cart-subtotal') || '0');
          }
          const discountsContainer = document.querySelector('[data-discounts]');
          const newDiscounts = doc.querySelector('.cart__discounts');
          if (discountsContainer && newDiscounts) {
            discountsContainer.innerHTML = newDiscounts.outerHTML;
          }
          const subtotalElement = document.querySelector('[data-subtotal]');
          if (subtotalElement && newProducts) {
            const newSubtotal = newProducts.getAttribute('data-cart-subtotal');
            if (newSubtotal && window.theme && window.theme.Currency) {
              subtotalElement.innerHTML = window.theme.Currency.formatMoney(newSubtotal, window.theme.settings.moneyFormat);
            }
          }
          if (cartDrawerForm && window.theme.CartForm) {
            let cartFormInstance = cartDrawerForm.cartFormInstance;
            if (!cartFormInstance) {
              cartFormInstance = new window.theme.CartForm(cartDrawerForm);
              cartDrawerForm.cartFormInstance = cartFormInstance;
            } else if (cartFormInstance.reInit) {
              cartFormInstance.reInit();
            }
          }
        }

      } catch (error) {
      }
    }

    async #showPopdownNotification(cartData) {
      try {
        const popdownWrapper = document.querySelector('[data-product-add-popdown-wrapper]');
        if (!popdownWrapper) return;

        const variantId = this.addButton.getAttribute('data-variant-id');
        const rootUrl = window.theme?.routes?.root_url || '/';
        const url = `${rootUrl}variants/${variantId}/?section_id=api-product-popdown`;

        const response = await fetch(url);
        if (!response.ok) return;

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const popdownContent = doc.querySelector('[data-api-content]');

        if (popdownContent) {
          popdownWrapper.innerHTML = popdownContent.innerHTML;
          popdownWrapper.classList.add('is-visible');

          setTimeout(() => {
            popdownWrapper.classList.remove('is-visible');
          }, 5000);

          const closeButton = popdownWrapper.querySelector('[data-close-popdown]');
          if (closeButton) {
            closeButton.addEventListener('click', () => {
              popdownWrapper.classList.remove('is-visible');
            });
          }
        }
      } catch (error) {
      }
    }

    #setLoadingState(loading) {
      if (loading) {
        this.addButton.classList.add('cart-upsell__button--loading');
        this.addButton.disabled = true;
      } else {
        this.addButton.classList.remove('cart-upsell__button--loading');
        const variantId = this.addButton.getAttribute('data-variant-id');
        this.addButton.disabled = !variantId;
      }
    }

    #showSuccess() {
      const originalText = this.buttonText.textContent;
      const successText = this.getAttribute('data-added-text') || 'Added to cart';

      this.buttonText.textContent = successText;

      setTimeout(() => {
        this.buttonText.textContent = originalText;
      }, 2000);
    }

    #showError() {
      const originalText = this.buttonText.textContent;
      const errorText = this.getAttribute('data-error-text') || 'Error';

      this.buttonText.textContent = errorText;

      setTimeout(() => {
        this.buttonText.textContent = originalText;
      }, 3000);
    }

    #showWidget() {
      if (this.widget) {
        this.widget.style.display = '';
        this.style.display = '';
      }
      if (this.titleMain) {
        this.titleMain.style.display = '';
      }
    }

    #hideWidget() {
      if (this.widget) {
        this.widget.style.display = 'none';
        this.style.display = 'none';
      }
      if (this.titleMain) {
        this.titleMain.style.display = 'none';
      }
    }

    #truncateText(text, maxLength) {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + '...';
    }

    #formatPrice(price, compareAtPrice) {
      const currencyCode = this.getAttribute('data-currency') || 'USD';
      const currencySymbol = this.getAttribute('data-currency-symbol') || '$';

      const formatMoney = (cents) => {
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents);
        }
        if (window.theme && window.theme.Currency && window.theme.settings && window.theme.settings.moneyFormat) {
          return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
        }
        try {
          return new Intl.NumberFormat('en', {
            style: 'currency',
            currency: currencyCode
          }).format(cents / 100);
        } catch (error) {
          return `${currencySymbol}${(cents / 100).toFixed(2)}`;
        }
      };

      let priceHtml = `<span class="cart-upsell__price-value">${formatMoney(price)}</span>`;

      if (compareAtPrice && compareAtPrice > price) {
        priceHtml += ` <span class="cart-upsell__price-compare-at">${formatMoney(compareAtPrice)}</span>`;
      }

      return priceHtml;
    }
  }

  customElements.define('cart-upsell-widget', CartUpsellWidget);
}
